import DOMPurify from 'dompurify';

/**
 * Client-side HTML sanitization (defense-in-depth).
 *
 * The API already server-sanitizes story bodies, event/activity descriptions,
 * and CMS richtext blocks. This wraps DOMPurify so that anything rendered via
 * `dangerouslySetInnerHTML` is scrubbed again on the client:
 *   - strips all `on*` event-handler attributes
 *   - blocks `javascript:` and other script-bearing URIs
 *   - allows common formatting, links, and images only
 *
 * `data:` URIs are disallowed for safety (covers data: script vectors); inline
 * base64 images are uncommon in this app's content and are intentionally dropped.
 */

const ALLOWED_TAGS = [
  'a', 'b', 'strong', 'i', 'em', 'u', 's', 'strike', 'sub', 'sup', 'br', 'hr',
  'p', 'span', 'div', 'blockquote', 'pre', 'code',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
  'img', 'figure', 'figcaption',
];

const ALLOWED_ATTR = [
  'href', 'title', 'target', 'rel',
  'src', 'alt', 'width', 'height', 'loading',
  'class', 'colspan', 'rowspan', 'align',
];

const CONFIG = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  // Permit http(s)/mailto/tel and relative URLs; block javascript:, data:, etc.
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|ftp):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
   FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['style'],
  ALLOW_DATA_ATTR: false,
};

// Force safe link behaviour: external links open with no referrer leakage.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/**
 * Sanitize an untrusted HTML string into a safe HTML string.
 * @param {string} dirty raw HTML
 * @returns {string} sanitized HTML safe for dangerouslySetInnerHTML
 */
export function sanitize(dirty) {
  if (dirty == null) return '';
  return DOMPurify.sanitize(String(dirty), CONFIG);
}

export default sanitize;
